import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
} from "reactstrap";
import { createSliderAPI, getSliderByIdAPI, updateSliderAPI } from "../../../../../api/slider.service.ts";
import Content from "../../../../../layout/content/Content";
import Head from "../../../../../layout/head/Head";

const SliderForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        title: "",
        subtitle: "",
        button: { label: "", actionURL: "" },
        sequence: 0,
        isHidden: false,
    });
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const isEditMode = !!id;

    useEffect(() => {
        if (isEditMode) {
            const fetchSlider = async () => {
                try {
                    setLoading(true);
                    const response = await getSliderByIdAPI({ id });
                    const slider = response.data.data;
                    setFormData({
                        title: slider.title,
                        subtitle: slider.subtitle,
                        button: {
                            label: slider.button.label,
                            actionURL: slider.button.actionURL,
                        },
                        sequence: slider.sequence || 0,
                        isHidden: slider.isHidden || false,
                    });
                    setImagePreview(slider.imageUrl);
                } catch (error) {
                    toast.error(`Failed to fetch slider: ${error.message}`, { position: "top-center" });
                } finally {
                    setLoading(false);
                }
            };
            fetchSlider();
        }
    }, [id]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name.includes("button.")) {
            const buttonField = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                button: { ...prev.button, [buttonField]: value },
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: type === "checkbox" ? checked : value,
            }));
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const { title, subtitle, button, sequence, isHidden } = formData;
        if (!title || !subtitle || !button.label || !button.actionURL || (!imageFile && !isEditMode)) {
            toast.error("Please fill in all required fields and select an image.", {
                position: "top-center",
            });
            setSubmitting(false);
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("data", JSON.stringify({ title, subtitle, button, sequence: Number(sequence), isHidden }));
        if (imageFile) {
            formDataToSend.append("images", imageFile);
        }

        try {
            if (isEditMode) {
                await updateSliderAPI({ id, formData: formDataToSend });
                toast.success("Slider updated successfully", { position: "top-center" });
            } else {
                await createSliderAPI({ formData: formDataToSend });
                toast.success("Slider created successfully", { position: "top-center" });
            }
            navigate("/sliders");
        } catch (error) {
            toast.error(`Failed to ${isEditMode ? "update" : "create"} slider: ${error.message}`, {
                position: "top-center",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <React.Fragment>
            <Head title={isEditMode ? "Edit Slider" : "Add Slider"} />
            <Content>
                <Card>
                    <CardHeader>
                        <h3>{isEditMode ? "Edit Slider" : "Add New Slider"}</h3>
                    </CardHeader>
                    <CardBody>
                        {loading && isEditMode ? (
                            <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
                                <Spinner color="primary" />
                                <span className="ms-2">Loading slider data...</span>
                            </div>
                        ) : (
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="title">Title *</Label>
                                            <Input
                                                type="text"
                                                id="title"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleInputChange}
                                                placeholder="Enter slider title"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="subtitle">Subtitle *</Label>
                                            <Input
                                                type="text"
                                                id="subtitle"
                                                name="subtitle"
                                                value={formData.subtitle}
                                                onChange={handleInputChange}
                                                placeholder="Enter slider subtitle"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="buttonLabel">Button Label *</Label>
                                            <Input
                                                type="text"
                                                id="buttonLabel"
                                                name="button.label"
                                                value={formData.button.label}
                                                onChange={handleInputChange}
                                                placeholder="Enter button label"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="buttonActionURL">Button Action URL *</Label>
                                            <Input
                                                type="url"
                                                id="buttonActionURL"
                                                name="button.actionURL"
                                                value={formData.button.actionURL}
                                                onChange={handleInputChange}
                                                placeholder="Enter button action URL"
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="sequence">Sequence</Label>
                                            <Input
                                                type="number"
                                                id="sequence"
                                                name="sequence"
                                                value={formData.sequence}
                                                onChange={handleInputChange}
                                                min="0"
                                                placeholder="Enter sequence number"
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="6">
                                        <FormGroup check className="mt-4">
                                            <Input
                                                type="checkbox"
                                                id="isHidden"
                                                name="isHidden"
                                                checked={formData.isHidden}
                                                onChange={handleInputChange}
                                            />
                                            <Label for="isHidden" check>
                                                Hide Slider
                                            </Label>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md="6">
                                        <FormGroup>
                                            <Label for="image">Image *{isEditMode && " (Leave empty to keep existing)"}</Label>
                                            <Input
                                                type="file"
                                                id="image"
                                                accept="image/*"
                                                onChange={handleImageChange}
                                                required={!isEditMode}
                                            />
                                        </FormGroup>
                                        {imagePreview && (
                                            <div className="mt-2">
                                                <img
                                                    src={imagePreview}
                                                    alt="Preview"
                                                    className="img-fluid rounded"
                                                    style={{ maxWidth: "200px", maxHeight: "100px", objectFit: "cover" }}
                                                />
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                                <Row className="mt-4">
                                    <Col>
                                        <Button color="primary" type="submit" disabled={submitting}>
                                            {submitting ? (
                                                <>
                                                    <Spinner size="sm" />
                                                    <span> {isEditMode ? "Updating..." : "Creating..."}</span>
                                                </>
                                            ) : (
                                                isEditMode ? "Update Slider" : "Create Slider"
                                            )}
                                        </Button>
                                        <Button
                                            color="secondary"
                                            type="button"
                                            onClick={() => navigate("/sliders")}
                                            className="ms-2"
                                        >
                                            Cancel
                                        </Button>
                                    </Col>
                                </Row>
                            </Form>
                        )}
                    </CardBody>
                </Card>
            </Content>
        </React.Fragment>
    );
};

export default SliderForm;